import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32 px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-500 mb-4">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">您访问的文章可能已被移除或地址不正确</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line mr-1.5"></i>返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    author: { '@type': 'Organization', name: '禁漫天堂 JMComic 官方指南' },
    publisher: { '@type': 'Organization', name: '禁漫天堂 JMComic 官方指南' },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://jmcomic-guide.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmcomic-guide.com/' },
      { '@type': 'ListItem', position: 2, name: '使用教程', item: 'https://jmcomic-guide.com/article/' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 禁漫天堂 JMComic 使用指南`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://jmcomic-guide.com/article/${article.slug}`}
        ogType="article"
      />
      <SchemaOrg schema={articleSchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">
                  <i className="ri-calendar-line mr-1"></i>{article.publishDate}
                </span>
                <span className="text-xs text-foreground-500">
                  <i className="ri-time-line mr-1"></i>{article.readTime}阅读
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-background-200
                [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:text-sm [&_p]:md:text-base
                [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:text-sm [&_ul]:md:text-base [&_ul]:space-y-1
                [&_ol]:mb-4 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:text-sm [&_ol]:md:text-base [&_ol]:space-y-2
                [&_li]:text-foreground-700 [&_strong]:text-foreground-900 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 p-6 rounded-2xl bg-secondary-50 border border-secondary-200/50">
              <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">相关文章推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 2)
                  .map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="flex items-start gap-3 p-3 rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-300/40 transition-all cursor-pointer group"
                    >
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-16 h-12 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </div>
                        <div className="text-xs text-foreground-500 mt-1">{related.publishDate}</div>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}